import React, { useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../../store';
import { updateStructureTemplate, getStructureTemplate, createBrandingTemplate, updateBrandingTemplate, getBrandingTemplate, createStructureTemplate, extractBrandingFromDocument } from '../../store/actions/TemplateActions';
import { useHistory } from 'react-router-dom';
import { useFormik, FieldArray, FormikProvider } from 'formik';
import * as Yup from 'yup';
import FormShell from '../shared/FormComponents/FormShell';
import Button from '../shared/TailwindComponents/Button';
import Input from '../shared/TailwindComponents/Input';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import {
  PiCaretLeftLight, PiCheckLight, PiDotsThreeVerticalLight, PiPlusLight, PiTrashLight,
  PiUploadSimpleLight, PiArrowLeftLight, PiWarningLight, PiCheckCircleLight,
  PiPencilSimpleLight, PiUsersLight, PiEyeLight, PiSidebarLight, PiXLight,
  PiMagicWandLight,
} from 'react-icons/pi';
import FileUploader from '../shared/TailwindComponents/FileUploader';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import { addTemplateCollaborator, removeTemplateCollaborator, updateTemplateCollaboratorAccess } from '../../store/actions/BatchTables/BatchTablesActions';
import Badge from '../shared/TailwindComponents/Badge';
import BrandingPreview from './BrandingPreview';
import DetailFrame from '../shared/Frames/DetailFrame';
import ProjectBadges from '../Projects/ProjectBadges';
import PendingAccessRequests from '../Projects/PendingAccessRequests';
import { Menu, Transition } from '@headlessui/react';
import BrandingPanel from './BrandingSection';
import BrandingReapplyBanner from './BrandingReapplyBanner';
import { fetchAllEmployees } from '../../store/actions/organization/employeeActions';
import { fetchLinkableProjects } from '../../store/actions/Projects/ProjectActions';
import { getInitials } from '../shared/TailwindComponents/CollaboratorsBadge';
import { normalise } from '../shared/access/accessModel';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import CollaboratorSelector, { Collaborator } from '../shared/CollaboratorSelector';
import OrgWideToggle from '../shared/FormComponents/OrgWideToggle';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import OrdinalBadge from '../shared/brand/OrdinalBadge';
import { toErrorText } from '../../utils/errorMessage';

interface TemplateFormProps {
  templateId?: string;
  type: 'structure' | 'branding';
}

export type HeadingKey = 'H1' | 'H2' | 'H3' | 'H4' | 'Body';

export type TypographyLevel = {
  FontType?: string;
  Size?: number | string;
  Color?: string;
};

interface FormValues {
  TemplateName: string;
  DraftType: string;
  OwnerType: string;
  SharedWith: ShareUser[];
  Structure: { section: string; subsections: string[] }[];
  Branding: {
    Enabled: boolean;
    PaperSize: 'A4' | 'Letter';
    FirstPageDifferent: boolean;

    Typography: {
      FontType: string;
      DocumentFontSize: number;
      HeadingFontSize: string | number;
      LineSpacing: number;
      ParagraphSpacingBefore: number;
      ParagraphSpacingAfter: number;
      Alignment: 'left' | 'justify' | 'right' | 'center';
      Levels: Record<HeadingKey, TypographyLevel>;
    };

    HeadingNumbering: {
      Style: 'none' | 'decimal' | 'legal';
    };

    PageNumber: {
      Enabled: boolean;
      Format: 'none' | 'X' | 'X of Y' | 'Page X' | 'Page X of Y';
      Position: 'footer-center' | 'footer-right' | 'footer-left' | 'header-center' | 'header-right' | 'header-left';
      StartAt: number;
    };

    Letterhead: {
      Enabled: boolean;
      FirmName: string;
      AddressLines: string;
      OrgNumber: string;
      Contact: string;
    };

    Confidentiality: {
      Enabled: boolean;
      Text: string;
      Placement: 'first-page' | 'header' | 'footer';
    };

    Margins: {
      Top: number;
      Bottom: number;
      Left: number;
      Right: number;
    };

    TableOfContents: {
      Enabled: boolean;
      Depth: number;
      Style: 'dotted' | 'dashed' | 'none';
    };

    Logos: {
      UseFirmLogo: boolean;
      FirmLogo: string | File;
      FirmLogoSize?: number;
      Color: boolean;

      UseWatermark: boolean;
      WatermarkLogo: string | File;
      WatermarkText?: string;
      WatermarkOpacity?: number;
    };

    HeaderFooter: {
      HeaderTextLeft: string;
      HeaderTextCenter: string;
      HeaderTextRight: string;
      FooterTextLeft: string;
      FooterTextCenter: string;
      FooterTextRight: string;
      ColorPalette: string[];
      HeaderMode: 'logo' | 'text' | 'both';
      FooterMode: 'logo' | 'text' | 'both';
      // Per-position modes (each can be 'text', 'logo', 'image', or 'none')
      HeaderLeftMode: 'text' | 'logo' | 'image' | 'none';
      HeaderCenterMode: 'text' | 'logo' | 'image' | 'none';
      HeaderRightMode: 'text' | 'logo' | 'image' | 'none';
      FooterLeftMode: 'text' | 'logo' | 'image' | 'none';
      FooterCenterMode: 'text' | 'logo' | 'image' | 'none';
      FooterRightMode: 'text' | 'logo' | 'image' | 'none';
      // Per-position images
      HeaderLeftImage: string | File;
      HeaderCenterImage: string | File;
      HeaderRightImage: string | File;
      FooterLeftImage: string | File;
      FooterCenterImage: string | File;
      FooterRightImage: string | File;
      HeaderHeading: string;
      FooterHeading: string;
      HeaderFontType?: string;
      FooterFontType?: string;
      HeaderFontSize: number;
      FooterFontSize: number;
      HeaderLogoSize?: number;
      FooterLogoSize?: number;
    };

    Automation: {
      PrebuiltTemplates: string[];
    },
  }
  type: 'structure' | 'branding';
}

function isFile(value: string | File): value is File {
  return value instanceof File;
}

export type AccessLevel = "view" | "edit";
export type ShareUser = {
  id: string;
  name: string;
  email: string;
  role: string;
  initials: string,
  avatarUrl: string;
  access?: AccessLevel;

};

const TemplateForm: React.FC<TemplateFormProps> = ({ templateId, type }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { checkSectionGate, checkAiFeatureGate } = useLicenseGate();
  const userInfo = useSelector((state: RootState) => state.user.userInfo);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const { employees } = useSelector((state: RootState) => state.employees);
  const [firmEmployees, setFirmEmployees] = useState<ShareUser[]>([]);
  const isEditing = !!templateId;

  // Branding extraction state
  const [creationMode, setCreationMode] = useState<'choose' | 'manual' | 'upload'>('choose');
  const [collaborators, setCollaborators] = useState<Collaborator[]>([]);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractedConfidence, setExtractedConfidence] = useState<Record<string, string> | null>(null);
  const [extractedFilename, setExtractedFilename] = useState<string>('');
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [templateFile, setTemplateFile] = useState<File | null>(null);  // Store uploaded template for submission
  const [htmlPreview, setHtmlPreview] = useState<string>('');  // Server-side HTML preview
  const [originalFileRef, setOriginalFileRef] = useState<string | null>(null);  // Blob ref for original file
  const [showPreviewPanel, setShowPreviewPanel] = useState(true);  // Toggle inline preview panel
  const [isOrgWide, setIsOrgWide] = useState(false);  // Organization-wide sharing toggle
  const [owner, setOwner] = useState<any>(null);  // Template owner from API
  const [projectOptions, setProjectOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedProjectUUID, setSelectedProjectUUID] = useState<string>('');

  useEffect(() => {
    if (templateId) return;
    (async () => {
      try {
        const projects = await dispatch(fetchLinkableProjects() as any);
        if (projects && Array.isArray(projects)) {
          setProjectOptions(projects.map((p: any) => ({ value: p.uuid, label: p.title })));
        }
      } catch {
        // non-critical
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId]);

  useEffect(() => {
    if (!employees || employees.length === 0) {
      dispatch(fetchAllEmployees({ sortOrder: 'desc' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (employees.length > 0) {
      setFirmEmployees(employees.filter((f) => f.UserID !== userInfo.UserID).map((e) => {
        return {
          id: e.UserID,
          name: `${e.FirstName} ${e.LastName}`,
          email: e.Email,
          role: e.Role,
          avatarUrl: normalise(e).avatar || '',
          initials: getInitials(`${e.FirstName} ${e.LastName}`)
        }
      }))
    }

  }, [employees, userInfo.UserID])


  // Base font options - static list of common legal/professional fonts
  const baseFontOptions = [
    // Serif fonts - Traditional legal document fonts
    { value: 'Times New Roman', label: 'Times New Roman' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Garamond', label: 'Garamond' },
    { value: 'Palatino Linotype', label: 'Palatino Linotype' },
    { value: 'Book Antiqua', label: 'Book Antiqua' },
    { value: 'Century Schoolbook', label: 'Century Schoolbook' },
    { value: 'Cambria', label: 'Cambria' },
    { value: 'Baskerville', label: 'Baskerville' },
    { value: 'Bookman Old Style', label: 'Bookman Old Style' },
    { value: 'Century', label: 'Century' },
    // Sans-serif fonts - Modern legal document fonts
    { value: 'Arial', label: 'Arial' },
    { value: 'Calibri', label: 'Calibri' },
    { value: 'Helvetica', label: 'Helvetica' },
    { value: 'Verdana', label: 'Verdana' },
    { value: 'Tahoma', label: 'Tahoma' },
    { value: 'Trebuchet MS', label: 'Trebuchet MS' },
    { value: 'Franklin Gothic', label: 'Franklin Gothic' },
    // Monospace fonts - For code/contracts with fixed-width needs
    { value: 'Courier New', label: 'Courier New' },
    { value: 'Consolas', label: 'Consolas' },
  ];

  const headingStyleOptions = [
    { value: '', label: t('Select Heading Style') },
    { value: 'Heading1', label: t('Heading 1') },
    { value: 'Heading2', label: t('Heading 2') },
    { value: 'Heading3', label: t('Heading 3') },
    { value: 'Heading4', label: t('Heading 4') },
    { value: 'Heading5', label: t('Heading 5') },
  ];

  const draftTypeOptions = [
    { value: '', label: t('Select Document Type') },
    { value: '*', label: t('All Types') },
    { value: 'agreement', label: t('Agreement') },
    { value: 'brief', label: t('Brief') },
    { value: 'complaint', label: t('Complaint') },
    { value: 'contract', label: t('Contract') },
    { value: 'correspondence', label: t('Correspondence') },
    { value: 'court-filing', label: t('Court Filing') },
    { value: 'discovery', label: t('Discovery') },
    { value: 'engagement-letter', label: t('Engagement Letter') },
    { value: 'legal-opinion', label: t('Legal Opinion') },
    { value: 'memo', label: t('Memo') },
    { value: 'motion', label: t('Motion') },
    { value: 'nda', label: t('NDA') },
    { value: 'pleading', label: t('Pleading') },
    { value: 'policy', label: t('Policy') },
    { value: 'power-of-attorney', label: t('Power of Attorney') },
    { value: 'proposal', label: t('Proposal') },
    { value: 'report', label: t('Report') },
    { value: 'settlement', label: t('Settlement') },
    { value: 'will-trust', label: t('Will / Trust') },
    { value: 'other', label: t('Other') },
  ];

  const ownerTypeOptions = [
    { value: '', label: t('Select Owner Type') },
    { value: 'personal', label: t('Personal') },
    { value: 'firm-wide', label: t('Firm-Wide') },
  ];

  const HeadingKeys = ['H1', 'H2', 'H3', 'H4', 'Body'] as const;
  const TypographyLevelSchema = Yup.object({
    FontType: Yup.string().nullable(),
    Size: Yup.mixed<string | number>()
      .test('size-auto-or-number', t('Enter "auto" or a number'), (val) => {
        if (val === undefined || val === null || val === '') return true;
        if (typeof val === 'string') return val.toLowerCase() === 'auto' || !isNaN(Number(val));
        if (typeof val === 'number') return true;
        return false;
      })
      .nullable(),
    Color: Yup.string()
      .matches(/^#([0-9A-Fa-f]{3}){1,2}([0-9A-Fa-f]{2})?$/, t('Enter a valid hex color'))
      .nullable(),
  });

  const validationSchema = Yup.object({
    TemplateName: Yup.string().required(t('Name is required')),
    DraftType: Yup.string().required(t('Document Type is required')),
    OwnerType: Yup.string().optional(),
    Structure: Yup.array().when('type', {
      is: 'structure',
      then: (schema) => schema.min(1, t('At least one section is required')).of(
        Yup.object({
          section: Yup.string().required(t('Section name is required')),
          subsections: Yup.array().of(Yup.string()),
        })
      ),
      otherwise: (schema) => schema.optional(),
    }),
    Branding: Yup.object().when('type', {
      is: 'branding',
      then: (schema) =>
        schema.shape({
          Typography: Yup.object({
            FontType: Yup.string().required(t('Font Type is required')),
            DocumentFontSize: Yup.number()
              .min(8, t('Font size must be between 8 and 20'))
              .max(20, t('Font size must be between 8 and 20'))
              .required(t('Font size is required')),
            HeadingFontSize: Yup.mixed<string | number>()
              .test('heading-size', t('Enter "auto" or a number between 10 and 24'), (val) => {
                if (val === undefined || val === null || val === '') return false;
                if (typeof val === 'string') {
                  if (val.toLowerCase() === 'auto') return true;
                  const n = Number(val);
                  return !isNaN(n) && n >= 10 && n <= 24;
                }
                if (typeof val === 'number') return val >= 10 && val <= 24;
                return false;
              })
              .required(t('Heading font size is required')),

            Levels: Yup.object(
              HeadingKeys.reduce((acc, key) => {
                acc[key] = TypographyLevelSchema;
                return acc;
              }, {} as Record<(typeof HeadingKeys)[number], typeof TypographyLevelSchema>)
            ),
          }),

          Margins: Yup.object({
            Top: Yup.number().min(0.5, t('Margin must be at least 0.5 inches')).required(t('Top margin is required')),
            Bottom: Yup.number().min(0.5, t('Margin must be at least 0.5 inches')).required(t('Bottom margin is required')),
            Left: Yup.number().min(0.3, t('Margin must be at least 0.5 inches')).required(t('Left margin is required')),
            Right: Yup.number().min(0.3, t('Margin must be at least 0.5 inches')).required(t('Right margin is required')),
          }),

          Logos: Yup.object({
            UseFirmLogo: Yup.boolean().default(false),
            FirmLogoSize: Yup.number().min(12).max(96).nullable(),
            Color: Yup.boolean().default(true),
            UseWatermark: Yup.boolean().default(false),
            WatermarkText: Yup.string().nullable(),
            WatermarkLogo: Yup.mixed().nullable(),
            WatermarkOpacity: Yup.number()
              .when('UseWatermark', {
                is: true,
                then: (s) =>
                  s.min(10, t('Opacity must be between 10 and 50'))
                    .max(50, t('Opacity must be between 10 and 50'))
                    .required(t('Watermark opacity is required')),
                otherwise: (s) => s.notRequired().nullable(),
              }),
          }),

          HeaderFooter: Yup.object({
            ColorPalette: Yup.array().of(
              Yup.string().matches(/^#([0-9A-Fa-f]{3}){1,2}([0-9A-Fa-f]{2})?$/, t('Enter a valid hex color'))
            ).default([]),
            HeaderMode: Yup.mixed<'logo' | 'text' | 'both'>().oneOf(['logo', 'text', 'both']).required(t('Header mode is required')),
            FooterMode: Yup.mixed<'logo' | 'text' | 'both'>().oneOf(['logo', 'text', 'both']).required(t('Footer mode is required')),
            HeaderTextLeft: Yup.string().nullable(),
            HeaderTextCenter: Yup.string().nullable(),
            HeaderTextRight: Yup.string().nullable(),
            FooterTextLeft: Yup.string().nullable(),
            FooterTextCenter: Yup.string().nullable(),
            FooterTextRight: Yup.string().nullable(),
            HeaderFontType: Yup.string().nullable(),
            FooterFontType: Yup.string().nullable(),
            HeaderFontSize: Yup.number().min(8).max(20).required(t('Header font size is required')),
            FooterFontSize: Yup.number().min(8).max(20).required(t('Footer font size is required')),

            HeaderLogoSize: Yup.number()
              .when('HeaderMode', {
                is: (m: string) => m === 'logo' || m === 'both',
                then: (s) => s.min(12).max(96).required(t('Header logo size is required')),
                otherwise: (s) => s.notRequired().nullable(),
              }),
            FooterLogoSize: Yup.number()
              .when('FooterMode', {
                is: (m: string) => m === 'logo' || m === 'both',
                then: (s) => s.min(12).max(96).required(t('Footer logo size is required')),
                otherwise: (s) => s.notRequired().nullable(),
              }),

            HeaderHeading: Yup.string(),
            FooterHeading: Yup.string(),
          }),

          Automation: Yup.object({
            PrebuiltTemplates: Yup.array().of(Yup.string()),
          }),
        }),
      otherwise: (schema) => schema.optional(),
    })
  });

  const formik = useFormik<FormValues>({
    initialValues: {
      TemplateName: '',
      DraftType: '',
      OwnerType: 'personal',
      SharedWith: [],
      Structure: [{ section: '', subsections: [] }],
      Branding: {
        Enabled: true,
        PaperSize: 'A4',
        FirstPageDifferent: false,
        Typography: {
          FontType: '',
          DocumentFontSize: 12,
          HeadingFontSize: 'auto',
          LineSpacing: 1.5,
          ParagraphSpacingBefore: 0,
          ParagraphSpacingAfter: 6,
          Alignment: 'left',
          Levels: { H1: {}, H2: {}, H3: {}, H4: {}, Body: {} },
        },
        HeadingNumbering: { Style: 'none' },
        PageNumber: { Enabled: false, Format: 'Page X of Y', Position: 'footer-center', StartAt: 1 },
        Letterhead: { Enabled: false, FirmName: '', AddressLines: '', OrgNumber: '', Contact: '' },
        Confidentiality: { Enabled: false, Text: '', Placement: 'first-page' },
        Margins: { Top: 1, Bottom: 1, Left: 1, Right: 1 },
        TableOfContents: { Enabled: false, Depth: 3, Style: 'dotted' },
        Logos: {
          UseFirmLogo: false,
          FirmLogo: '',
          FirmLogoSize: 24,
          Color: true,
          UseWatermark: false,
          WatermarkLogo: '',
          WatermarkText: 'DRAFT',
          WatermarkOpacity: 25,
        },
        HeaderFooter: {
          HeaderMode: 'text',
          FooterMode: 'text',
          // Per-position modes
          HeaderLeftMode: 'text',
          HeaderCenterMode: 'none',
          HeaderRightMode: 'none',
          FooterLeftMode: 'text',
          FooterCenterMode: 'none',
          FooterRightMode: 'none',
          // Per-position images
          HeaderLeftImage: '',
          HeaderCenterImage: '',
          HeaderRightImage: '',
          FooterLeftImage: '',
          FooterCenterImage: '',
          FooterRightImage: '',
          HeaderTextLeft: '',
          HeaderTextCenter: '',
          HeaderTextRight: '',
          FooterTextLeft: '',
          FooterTextCenter: '',
          FooterTextRight: '',
          ColorPalette: [],
          HeaderHeading: '',
          FooterHeading: '',
          HeaderFontType: '',
          FooterFontType: '',
          HeaderFontSize: 10,
          FooterFontSize: 10,
          HeaderLogoSize: 20,
          FooterLogoSize: 18,
        },
        Automation: { PrebuiltTemplates: [] },
      },
      type,
    },
    validationSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      if (!checkSectionGate('templates')) return;
      if (!checkAiFeatureGate('template_ai')) return;
      setIsLoading(true);
      // Convert collaborators to SharedWith format (array of user IDs)
      const sharedWithIds = isOrgWide
        ? []
        : type === 'branding'
          ? collaborators.map(c => c.collaborator_uuid || c.user_id).filter(Boolean)
          : values.SharedWith;
      
      const data = {
        TemplateName: values.TemplateName,
        DraftType: values.DraftType,
        OwnerType: isOrgWide ? 'firm-wide' : (values.OwnerType || 'personal'),
        SharedWith: sharedWithIds,
        // Always send branding config - extraction populates form fields
        ...(type === 'structure' 
          ? { Structure: values.Structure.filter((s) => s.section.trim() !== '') } 
          : { Branding: values.Branding }),
        // Pass original file reference if extracted from upload
        ...(originalFileRef ? { original_file_ref: originalFileRef } : {}),
        ...(!templateId && selectedProjectUUID ? { project_uuid: selectedProjectUUID } : {}),
      };
      const files: { [key: string]: File } = {};
      if (type === 'branding') {
        if (isFile(values.Branding.Logos.FirmLogo)) {
          files.FirmLogo = values.Branding.Logos.FirmLogo;
        }
        if (isFile(values.Branding.Logos.WatermarkLogo)) {
          files.WatermarkLogo = values.Branding.Logos.WatermarkLogo;
        }
        // Include template file if user uploaded one (for storage as original reference)
        if (templateFile) {
          files.template = templateFile;
        }
      }
      if (templateId) {
        const action = type === 'structure'
          ? updateStructureTemplate
          : updateBrandingTemplate;
        await dispatch(action(templateId, data, files));
      } else {
        if (type === 'structure') {
          await dispatch(createStructureTemplate(data, files) as any);
        } else {
          await dispatch(createBrandingTemplate(undefined, data, files));
        }
      }
      setIsLoading(false);
      history.push(`/templates?type=${type}`);
    },
    enableReinitialize: true,
  });

  // Dynamic font options - includes any extracted font not in the base list
  const fontOptions = useMemo(() => {
    const knownValues = new Set(baseFontOptions.map(o => o.value.toLowerCase()));
    const extraFonts: { value: string; label: string }[] = [];

    // Collect all font values currently set in the branding config
    const branding = formik.values.Branding;
    const fontsInUse = new Set<string>();
    if (branding?.Typography?.FontType) fontsInUse.add(branding.Typography.FontType);
    if (branding?.Typography?.Levels) {
      Object.values(branding.Typography.Levels).forEach((level: any) => {
        if (level?.FontType) fontsInUse.add(level.FontType);
      });
    }
    if (branding?.HeaderFooter?.HeaderFontType) fontsInUse.add(branding.HeaderFooter.HeaderFontType);
    if (branding?.HeaderFooter?.FooterFontType) fontsInUse.add(branding.HeaderFooter.FooterFontType);

    // Add any font in use that's not already in the base list
    fontsInUse.forEach(fontName => {
      if (fontName && !knownValues.has(fontName.toLowerCase())) {
        extraFonts.push({ value: fontName, label: `${fontName} (extracted)` });
        knownValues.add(fontName.toLowerCase());
      }
    });

    return [
      { value: '', label: t('Select Font Type') },
      ...extraFonts,
      ...baseFontOptions,
    ];
  }, [formik.values.Branding?.Typography, formik.values.Branding?.HeaderFooter, t, baseFontOptions]);

  useEffect(() => {
    if (templateId && !fetchedRef.current) {
      fetchedRef.current = true;
      setIsLoading(true);
      setError(null);
      const getAction = type === 'structure' ? getStructureTemplate : getBrandingTemplate;
      dispatch(getAction(templateId))
        .then((template) => {
          if (template) {
            const structure = template.Structure && template.Structure.length > 0 ? template.Structure : [{ section: '', subsections: [] }];
            formik.setValues({
              TemplateName: template.TemplateName || '',
              DraftType: template.DraftType || '',
              OwnerType: template.OwnerType || 'personal',
              SharedWith: template.SharedWith || [],
              Structure: structure,
              Branding: {
                Enabled: template.Branding?.Enabled ?? true,
                PaperSize: template.Branding?.PaperSize ?? 'A4',
                FirstPageDifferent: template.Branding?.FirstPageDifferent ?? false,

                Typography: {
                  FontType: template.Branding?.Typography?.FontType ?? '',
                  DocumentFontSize: template.Branding?.Typography?.DocumentFontSize ?? 12,
                  HeadingFontSize: template.Branding?.Typography?.HeadingFontSize ?? 'auto',
                  LineSpacing: template.Branding?.Typography?.LineSpacing ?? 1.5,
                  ParagraphSpacingBefore: template.Branding?.Typography?.ParagraphSpacingBefore ?? 0,
                  ParagraphSpacingAfter: template.Branding?.Typography?.ParagraphSpacingAfter ?? 6,
                  Alignment: template.Branding?.Typography?.Alignment ?? 'left',

                  Levels: {
                    H1: template.Branding?.Typography?.Levels?.H1 ?? {},
                    H2: template.Branding?.Typography?.Levels?.H2 ?? {},
                    H3: template.Branding?.Typography?.Levels?.H3 ?? {},
                    H4: template.Branding?.Typography?.Levels?.H4 ?? {},
                    Body: template.Branding?.Typography?.Levels?.Body ?? {},
                  },
                },

                HeadingNumbering: {
                  Style: template.Branding?.HeadingNumbering?.Style ?? 'none',
                },

                Margins: {
                  Top: template.Branding?.Margins?.Top ?? 1,
                  Bottom: template.Branding?.Margins?.Bottom ?? 1,
                  Left: template.Branding?.Margins?.Left ?? 1,
                  Right: template.Branding?.Margins?.Right ?? 1,
                },

                TableOfContents: {
                  Enabled: template.Branding?.TableOfContents?.Enabled ?? false,
                  Depth: template.Branding?.TableOfContents?.Depth ?? 3,
                  Style: template.Branding?.TableOfContents?.Style ?? 'dotted',
                },

                Logos: {
                  UseFirmLogo: template.Branding?.Logos?.UseFirmLogo ?? false,
                  FirmLogo: template.Branding?.Logos?.FirmLogo ?? '',
                  FirmLogoSize: template.Branding?.Logos?.FirmLogoSize ?? 24,
                  Color: template.Branding?.Logos?.Color ?? true,
                  UseWatermark: template.Branding?.Logos?.UseWatermark ?? false,
                  WatermarkLogo: template.Branding?.Logos?.WatermarkLogo ?? '',
                  WatermarkText: template.Branding?.Logos?.WatermarkText ?? 'DRAFT',
                  WatermarkOpacity: template.Branding?.Logos?.WatermarkOpacity ?? 25,
                },
                HeaderFooter: {
                  HeaderTextLeft: template.Branding?.HeaderFooter?.HeaderTextLeft ?? template.Branding?.HeaderFooter?.HeaderText ?? '',
                  HeaderTextCenter: template.Branding?.HeaderFooter?.HeaderTextCenter ?? '',
                  HeaderTextRight: template.Branding?.HeaderFooter?.HeaderTextRight ?? '',
                  FooterTextLeft: template.Branding?.HeaderFooter?.FooterTextLeft ?? '',
                  FooterTextCenter: template.Branding?.HeaderFooter?.FooterTextCenter ?? '',
                  FooterTextRight: template.Branding?.HeaderFooter?.FooterTextRight ?? template.Branding?.HeaderFooter?.FooterText ?? '',
                  ColorPalette: template.Branding?.HeaderFooter?.ColorPalette ?? [],
                  HeaderMode: template.Branding?.HeaderFooter?.HeaderMode ?? 'text',
                  FooterMode: template.Branding?.HeaderFooter?.FooterMode ?? 'text',
                  // Per-position modes
                  HeaderLeftMode: template.Branding?.HeaderFooter?.HeaderLeftMode ?? 'text',
                  HeaderCenterMode: template.Branding?.HeaderFooter?.HeaderCenterMode ?? 'none',
                  HeaderRightMode: template.Branding?.HeaderFooter?.HeaderRightMode ?? 'none',
                  FooterLeftMode: template.Branding?.HeaderFooter?.FooterLeftMode ?? 'text',
                  FooterCenterMode: template.Branding?.HeaderFooter?.FooterCenterMode ?? 'none',
                  FooterRightMode: template.Branding?.HeaderFooter?.FooterRightMode ?? 'none',
                  // Per-position images
                  HeaderLeftImage: template.Branding?.HeaderFooter?.HeaderLeftImage ?? '',
                  HeaderCenterImage: template.Branding?.HeaderFooter?.HeaderCenterImage ?? '',
                  HeaderRightImage: template.Branding?.HeaderFooter?.HeaderRightImage ?? '',
                  FooterLeftImage: template.Branding?.HeaderFooter?.FooterLeftImage ?? '',
                  FooterCenterImage: template.Branding?.HeaderFooter?.FooterCenterImage ?? '',
                  FooterRightImage: template.Branding?.HeaderFooter?.FooterRightImage ?? '',
                  HeaderHeading: template.Branding?.HeaderFooter?.HeaderHeading ?? '',
                  FooterHeading: template.Branding?.HeaderFooter?.FooterHeading ?? '',
                  HeaderFontType:
                    template.Branding?.HeaderFooter?.HeaderFontType ??
                    template.Branding?.Typography?.HeaderFontType ?? 'Use Default',
                  FooterFontType:
                    template.Branding?.HeaderFooter?.FooterFontType ??
                    template.Branding?.Typography?.FooterFontType ?? "Use Default",
                  HeaderFontSize:
                    template.Branding?.HeaderFooter?.HeaderFontSize ??
                    template.Branding?.Typography?.HeaderFontSize ?? 10,
                  FooterFontSize:
                    template.Branding?.HeaderFooter?.FooterFontSize ??
                    template.Branding?.Typography?.FooterFontSize ?? 10,
                  HeaderLogoSize: template.Branding?.HeaderFooter?.HeaderLogoSize ?? 20,
                  FooterLogoSize: template.Branding?.HeaderFooter?.FooterLogoSize ?? 18,
                },
                PageNumber: {
                  Enabled: template.Branding?.PageNumber?.Enabled ?? false,
                  Format: template.Branding?.PageNumber?.Format ?? 'Page X of Y',
                  Position: template.Branding?.PageNumber?.Position ?? 'footer-center',
                  StartAt: template.Branding?.PageNumber?.StartAt ?? 1,
                },
                Letterhead: {
                  Enabled: template.Branding?.Letterhead?.Enabled ?? false,
                  FirmName: template.Branding?.Letterhead?.FirmName ?? '',
                  AddressLines: Array.isArray(template.Branding?.Letterhead?.AddressLines)
                    ? template.Branding.Letterhead.AddressLines.join('\n')
                    : (template.Branding?.Letterhead?.AddressLines ?? ''),
                  OrgNumber: template.Branding?.Letterhead?.OrgNumber ?? '',
                  Contact: template.Branding?.Letterhead?.Contact ?? '',
                },
                Confidentiality: {
                  Enabled: template.Branding?.Confidentiality?.Enabled ?? false,
                  Text: template.Branding?.Confidentiality?.Text ?? '',
                  Placement: template.Branding?.Confidentiality?.Placement ?? 'first-page',
                },
                Automation: {
                  PrebuiltTemplates: template.Branding?.Automation?.PrebuiltTemplates ?? [],
                },
              },
              type,
            });

            // Check if this is a file-based template and set appropriate mode
            if (template.UseTemplateFile) {
              const filename = template.TemplateFilename || template.template_filename || 'Original document';
              setExtractedFilename(filename);
              
              // Set org-wide state and owner from loaded template
              setIsOrgWide(template.OwnerType === 'firm-wide');
              if (template.Owner) setOwner(template.Owner);
              
              // Load collaborators from template SharedWith
              if (template.SharedWith && Array.isArray(template.SharedWith)) {
                const mappedCollaborators = template.SharedWith.map((user: any) => ({
                  user_id: user.UserID || user.id,
                  collaborator_uuid: user.UserID || user.id,
                  name: user.Name || `${user.FirstName || ''} ${user.LastName || ''}`.trim() || user.name || 'Unknown',
                  email: user.Email || user.email || '',
                  role: user.Role || user.role || '',
                  avatarUrl: normalise(user).avatar || '',
                  initials: user.Initials || user.initials || (user.Name || user.name || 'U').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase(),
                  access: (user.Access || user.access || 'edit') as 'view' | 'edit'
                }));
                setCollaborators(mappedCollaborators);
              }
              
              // Set mode to manual since branding config is now always stored as form values
              setCreationMode('manual');
            } else {
              setCreationMode('manual');
              
              // Set org-wide state and owner from loaded template
              setIsOrgWide(template.OwnerType === 'firm-wide');
              if (template.Owner) setOwner(template.Owner);
              
              // Load collaborators from template SharedWith
              if (template.SharedWith && Array.isArray(template.SharedWith)) {
                const mappedCollaborators = template.SharedWith.map((user: any) => ({
                  user_id: user.UserID || user.id,
                  collaborator_uuid: user.UserID || user.id,
                  name: user.Name || `${user.FirstName || ''} ${user.LastName || ''}`.trim() || user.name || 'Unknown',
                  email: user.Email || user.email || '',
                  role: user.Role || user.role || '',
                  avatarUrl: normalise(user).avatar || '',
                  initials: user.Initials || user.initials || (user.Name || user.name || 'U').split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase(),
                  access: (user.Access || user.access || 'edit') as 'view' | 'edit'
                }));
                setCollaborators(mappedCollaborators);
              }
            }

          } else {
            setError(t('Template not found'));
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(`Error fetching template ${templateId}:`, error);
          setError(t('Error loading template'));
          setIsLoading(false);
          dispatch(showFeedbackModal({
            modalType: 'error',
            message: t('Failed to load template. Please try again.'),
            duration: 3000,
            showModal: true
          }));
        });
    }
  }, [templateId, type, dispatch, formik, t]);

  // Handle document upload - extract branding and populate form fields
  const handleDocumentUpload = async (file: File) => {
    if (!file) return;

    // Validate file type
    const ext = file.name.toLowerCase().split('.').pop();
    if (!ext || !['docx', 'pdf'].includes(ext)) {
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Please upload a .docx or .pdf file'),
        duration: 3000,
        showModal: true
      }));
      return;
    }

    // Store the file for later submission
    setTemplateFile(file);
    setExtractedFilename(file.name);
    setIsExtracting(true);
    
    try {
      // Call extraction API - returns branding config + HTML preview
      const result = await dispatch(extractBrandingFromDocument(file));
      
      if (result?.success && result.branding) {
        // Populate all Formik fields from extracted branding
        const b = result.branding;
        
        formik.setValues({
          ...formik.values,
          Branding: {
            Enabled: b.Enabled ?? true,
            PaperSize: b.PaperSize ?? 'A4',
            FirstPageDifferent: b.FirstPageDifferent ?? false,
            Typography: {
              FontType: b.Typography?.FontType || '',
              DocumentFontSize: b.Typography?.DocumentFontSize ?? 12,
              HeadingFontSize: b.Typography?.HeadingFontSize ?? 'auto',
              LineSpacing: b.Typography?.LineSpacing ?? 1.5,
              ParagraphSpacingBefore: b.Typography?.ParagraphSpacingBefore ?? 0,
              ParagraphSpacingAfter: b.Typography?.ParagraphSpacingAfter ?? 6,
              Alignment: b.Typography?.Alignment ?? 'left',
              Levels: {
                H1: b.Typography?.Levels?.H1 ?? {},
                H2: b.Typography?.Levels?.H2 ?? {},
                H3: b.Typography?.Levels?.H3 ?? {},
                H4: b.Typography?.Levels?.H4 ?? {},
                Body: b.Typography?.Levels?.Body ?? {},
              },
            },
            HeadingNumbering: {
              Style: b.HeadingNumbering?.Style ?? 'none',
            },
            Margins: {
              Top: b.Margins?.Top ?? 1,
              Bottom: b.Margins?.Bottom ?? 1,
              Left: b.Margins?.Left ?? 1,
              Right: b.Margins?.Right ?? 1,
            },
            TableOfContents: {
              Enabled: b.TableOfContents?.Enabled ?? false,
              Depth: b.TableOfContents?.Depth ?? 3,
              Style: b.TableOfContents?.Style ?? 'dotted',
            },
            Logos: {
              UseFirmLogo: b.Logos?.UseFirmLogo ?? false,
              FirmLogo: b.Logos?.FirmLogo ?? '',
              FirmLogoSize: b.Logos?.FirmLogoSize ?? 24,
              Color: b.Logos?.Color ?? true,
              UseWatermark: b.Logos?.UseWatermark ?? false,
              WatermarkLogo: b.Logos?.WatermarkLogo ?? '',
              WatermarkText: b.Logos?.WatermarkText ?? '',
              WatermarkOpacity: b.Logos?.WatermarkOpacity ?? 25,
            },
            HeaderFooter: {
              HeaderMode: b.HeaderFooter?.HeaderMode ?? 'text',
              FooterMode: b.HeaderFooter?.FooterMode ?? 'text',
              HeaderLeftMode: b.HeaderFooter?.HeaderLeftMode ?? 'none',
              HeaderCenterMode: b.HeaderFooter?.HeaderCenterMode ?? 'none',
              HeaderRightMode: b.HeaderFooter?.HeaderRightMode ?? 'none',
              FooterLeftMode: b.HeaderFooter?.FooterLeftMode ?? 'none',
              FooterCenterMode: b.HeaderFooter?.FooterCenterMode ?? 'none',
              FooterRightMode: b.HeaderFooter?.FooterRightMode ?? 'none',
              HeaderLeftImage: b.HeaderFooter?.HeaderLeftImage ?? '',
              HeaderCenterImage: b.HeaderFooter?.HeaderCenterImage ?? '',
              HeaderRightImage: b.HeaderFooter?.HeaderRightImage ?? '',
              FooterLeftImage: b.HeaderFooter?.FooterLeftImage ?? '',
              FooterCenterImage: b.HeaderFooter?.FooterCenterImage ?? '',
              FooterRightImage: b.HeaderFooter?.FooterRightImage ?? '',
              HeaderTextLeft: b.HeaderFooter?.HeaderTextLeft ?? '',
              HeaderTextCenter: b.HeaderFooter?.HeaderTextCenter ?? '',
              HeaderTextRight: b.HeaderFooter?.HeaderTextRight ?? '',
              FooterTextLeft: b.HeaderFooter?.FooterTextLeft ?? '',
              FooterTextCenter: b.HeaderFooter?.FooterTextCenter ?? '',
              FooterTextRight: b.HeaderFooter?.FooterTextRight ?? '',
              ColorPalette: b.HeaderFooter?.ColorPalette ?? [],
              HeaderHeading: b.HeaderFooter?.HeaderHeading ?? '',
              FooterHeading: b.HeaderFooter?.FooterHeading ?? '',
              HeaderFontType: b.HeaderFooter?.HeaderFontType ?? '',
              FooterFontType: b.HeaderFooter?.FooterFontType ?? '',
              HeaderFontSize: b.HeaderFooter?.HeaderFontSize ?? 10,
              FooterFontSize: b.HeaderFooter?.FooterFontSize ?? 10,
              HeaderLogoSize: b.HeaderFooter?.HeaderLogoSize ?? 20,
              FooterLogoSize: b.HeaderFooter?.FooterLogoSize ?? 18,
            },
            PageNumber: {
              Enabled: b.PageNumber?.Enabled ?? false,
              Format: b.PageNumber?.Format ?? 'Page X of Y',
              Position: b.PageNumber?.Position ?? 'footer-center',
              StartAt: b.PageNumber?.StartAt ?? 1,
            },
            Letterhead: {
              Enabled: b.Letterhead?.Enabled ?? false,
              FirmName: b.Letterhead?.FirmName ?? '',
              AddressLines: Array.isArray(b.Letterhead?.AddressLines)
                ? b.Letterhead.AddressLines.join('\n')
                : (b.Letterhead?.AddressLines ?? ''),
              OrgNumber: b.Letterhead?.OrgNumber ?? '',
              Contact: b.Letterhead?.Contact ?? '',
            },
            Confidentiality: {
              Enabled: b.Confidentiality?.Enabled ?? false,
              Text: b.Confidentiality?.Text ?? '',
              Placement: b.Confidentiality?.Placement ?? 'first-page',
            },
            Automation: {
              PrebuiltTemplates: b.Automation?.PrebuiltTemplates ?? [],
            },
          },
        });

        // Store extraction metadata
        setExtractedConfidence(result.confidence || null);
        if (result.html_preview) {
          setHtmlPreview(result.html_preview);
        }
        if (result.original_file_ref) {
          setOriginalFileRef(result.original_file_ref);
        }
        
        // Transition to manual mode with pre-filled fields
        setCreationMode('manual');
        
        dispatch(showFeedbackModal({
          modalType: 'success',
          message: t('Branding extracted! Review and adjust the settings below.'),
          duration: 4000,
          showModal: true
        }));
      } else {
        // Extraction failed - go to manual mode anyway
        setCreationMode('manual');
        dispatch(showFeedbackModal({
          modalType: 'warning',
          message: toErrorText(result?.error) || t('Could not extract branding. Please configure manually.'),
          duration: 4000,
          showModal: true
        }));
      }
    } catch (err) {
      console.error('Extraction failed:', err);
      setCreationMode('manual');
      dispatch(showFeedbackModal({
        modalType: 'error',
        message: t('Extraction failed. Please configure branding manually.'),
        duration: 3000,
        showModal: true
      }));
    } finally {
      setIsExtracting(false);
    }
  };

  // Handle files change from FileUploader - NO auto-extraction
  const handleUploadFilesChange = (files: File[]) => {
    setUploadedFiles(files);
  };

  // Explicit extraction trigger - called by "Extract Branding" button
  const handleExtractClick = () => {
    if (uploadedFiles.length > 0) {
      handleDocumentUpload(uploadedFiles[0]);
    }
  };



  const templateBreadcrumbs = [
    { name: t('Templates'), href: '/templates', current: false },
    { name: formik.values.TemplateName || (templateId ? t('Edit Template') : t('Add Template')), href: templateId ? `/templates/${templateId}` : '/templates/add', current: true },
  ];

  const templateDropdownActions = [
    // Manage Collaborators - only when editing (when creating, it's inline)
    ...(isEditing ? [{
      label: t('Access Control'),
      onClick: () => dispatch(showSidePanel({
        type: 'manageResourceCollaborators',
        showSidePanel: true,
        size: 'md',
        message: t('Manage Access'),
        resourceUuid: templateId || '',
        resourceType: 'template',
        collaborators,
        currentUserUuid: userInfo?.UserUID,
        canAdmin: true,
        addCollaborator: async (_uuid: string, userUuid: string, access: string) => {
          await dispatch(addTemplateCollaborator(templateId || '', userUuid, access) as any);
        },
        removeCollaborator: async (_uuid: string, collabUuid: string) => {
          await dispatch(removeTemplateCollaborator(templateId || '', collabUuid) as any);
        },
        updateAccess: async (_uuid: string, collabUuid: string, access: string) => {
          await dispatch(updateTemplateCollaboratorAccess(templateId || '', collabUuid, access) as any);
        },
        onUpdate: () => {},
      } as any)),
      icon: <PiUsersLight className="h-4 w-4" />,
      disabled: isLoading,
    }] : []),
    // Full Preview - open preview in modal
    ...(type === 'branding' && (creationMode === 'manual' || isEditing) ? [{
      label: t('Full Preview'),
      onClick: () => dispatch(showModal({
        type: 'templatePreview',
        showModal: true,
        message: t('Template Preview'),
        templateBranding: formik.values.Branding,
        width: '90vw',
      })),
      icon: <PiEyeLight className="h-4 w-4" />,
      disabled: isLoading,
    }] : []),
  ];

  // SharedWith from API - filter to valid entries
  const sharedWithUsers = (formik.values.SharedWith || []).filter((s: any) => s.UserID || s.Initials || s.Email || s.Name);

  /**
   * Header faces, in the shape `accessModel` reads.
   *
   * The rows go through UNMAPPED - `Name` / `Email` / `ProfilePicture` /
   * `Initials` are all keys `accessModel.normalise` knows, so the owner's and
   * each collaborator's real picture survives. The previous version hand-rolled
   * a stack of badges and derived initials inline, which is why this header drew
   * "SM/AL" letters where every other detail page drew photos.
   *
   * `role: 'creator'` on the owner is what pins them first in the stack.
   */
  const headerCollaborators = [
    ...((owner?.UserID || owner?.Name || owner?.Email || userInfo?.FullName)
      ? [{
          ...(owner || {}),
          Name: owner?.Name || userInfo?.FullName || t('Owner'),
          Email: owner?.Email || userInfo?.Email,
          /*
            THE OWNER'S PICTURE, or none - never the viewer's.

            This read `owner?.ProfilePicture` and fell through to
            `userInfo.profile_picture`. The templates backend spells the
            owner's picture `profile_picture` (snake) - see
            `_enrich_shared_with_users` - so the first term was ALWAYS
            undefined and the fallback always fired: opening a colleague's
            template drew YOUR face as its creator.

            `normalise` reads every spelling, so the owner's own picture is
            found. The viewer's picture is only a sensible default when there
            is no owner row at all, i.e. a template being created right now,
            which is the case that fallback was written for.
          */
          ProfilePicture:
            normalise(owner || {}).avatar
            ?? (owner ? null : (userInfo?.profile_picture || userInfo?.ProfilePicture || null)),
          Initials: owner?.Initials,
          role: 'creator',
          access: 'admin',
        }]
      : []),
    ...sharedWithUsers.map((s: any) => ({ ...s, access: s.Access || 'edit' })),
  ];

  const openManageAccess = () => {
    if (!templateId) return;
    dispatch(showSidePanel({
      type: 'manageResourceCollaborators',
      showSidePanel: true,
      size: 'md',
      message: t('Manage Access'),
      resourceUuid: templateId,
      resourceType: 'template',
      collaborators,
      currentUserUuid: userInfo?.UserUID,
      canAdmin: true,
      addCollaborator: async (uuid: string, userUuid: string, access: string) => {
        await dispatch(addTemplateCollaborator(uuid, userUuid, access) as any);
      },
      removeCollaborator: async (uuid: string, collabUuid: string) => {
        await dispatch(removeTemplateCollaborator(uuid, collabUuid) as any);
      },
      updateAccess: async (uuid: string, collabUuid: string, access: string) => {
        await dispatch(updateTemplateCollaboratorAccess(uuid, collabUuid, access) as any);
      },
      onUpdate: () => {
        // Collaborators will be refreshed via the side panel
      },
    }));
  };

  // Header elements - conditional: full avatars + menu when editing, minimal when creating
  const additionalHeaderElements = isEditing ? (
    <div className="flex items-center gap-2">
      {templateId && <ProjectBadges resourceType="template" resourceUuid={templateId} />}
      {templateId && <PendingAccessRequests resourceType="template" resourceUuid={templateId} />}
      {/* Collaborator faces - the shared stack, clickable to manage access */}
      <div className="flex items-center group">
        <CollaboratorStack
          collaborators={headerCollaborators}
          max={4}
          size="md"
          onClick={openManageAccess}
          testId="template-collaborator-stack"
          className="hover:opacity-80 transition-opacity"
        />
        {/* Add indicator */}
        <Button
          variant="neutral"
          size="small"
          onClick={openManageAccess}
          tooltip={t('Manage Access')}
          ariaLabel={t('Manage Access')}
          className="!h-8 !w-8 !p-0 !rounded-full border-2 border-dashed !border-hairline !bg-transparent hover:!bg-surface-soft opacity-0 group-hover:opacity-100 transition-opacity ms-[-0.5rem]"
        >
          <PiPlusLight className="h-3 w-3 text-fg-muted" />
        </Button>
      </div>

      {/* Preview toggle (branding only) */}
      {type === 'branding' && (creationMode === 'manual' || isEditing) && (
        <Button
          variant="outline"
          size="small"
          className="p-1.5"
          onClick={() => setShowPreviewPanel(p => !p)}
          title={showPreviewPanel ? t('Hide Preview') : t('Show Preview')}
        >
          <PiSidebarLight className={`h-4 w-4 ${showPreviewPanel ? 'text-primary-600' : ''}`} />
        </Button>
      )}

      {/* Overflow menu */}
      {templateDropdownActions.length > 0 && (
        <Menu as="div" className="relative inline-block text-start z-50">
          <Menu.Button as={Button} variant="outline" size="small" className="p-1.5">
            <PiDotsThreeVerticalLight className="h-4 w-4" />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="transform opacity-100 scale-100"
            leave="transition ease-in duration-75"
            leaveFrom="transform opacity-100 scale-100"
            leaveTo="transform opacity-0 scale-95"
          >
            <Menu.Items className="absolute end-0 mt-2 w-52 origin-top-right rounded-lg glass shadow-lg focus:outline-none divide-y divide-hairline-soft">
              <div className="p-1">
                {templateDropdownActions.map((action, index) => (
                  <Menu.Item key={index}>
                    {({ active }) => (
                      <button
                        className={`${active ? 'bg-surface-soft' : ''} text-fg-body group flex w-full items-center rounded-md px-2.5 py-2 text-sm gap-2`}
                        onClick={action.onClick}
                        disabled={action.disabled}
                      >
                        {action.icon}
                        {t(action.label)}
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Transition>
        </Menu>
      )}
    </div>
  ) : (
    // Creating: just show preview toggle when in manual mode
    type === 'branding' && creationMode === 'manual' ? (
      <Button
        variant="outline"
        size="small"
        className="p-1.5"
        onClick={() => setShowPreviewPanel(p => !p)}
        title={showPreviewPanel ? t('Hide Preview') : t('Show Preview')}
      >
        <PiSidebarLight className={`h-4 w-4 ${showPreviewPanel ? 'text-primary-600' : ''}`} />
      </Button>
    ) : null
  );

  if (isLoading) return <Skeleton variant="form" rows={6} />;

  if (error) {
    return (
      <DetailFrame
        breadcrumbs={templateBreadcrumbs}
        dropdownActions={[]}
        subscriptionActive={true}
        additionalHeaderElements={null}
        showBackButton={true}
        className="p-6 text-center space-y-4"
      >
        <Badge color="gray">{error}</Badge>
        <Button variant="primary" size="small" onClick={() => history.push(`/templates?type=${type}`)}>
          {t('Back to Templates')}
        </Button>
      </DetailFrame>
    );
  }

  // Determine what content to show
  const showFormContent = creationMode === 'manual' || isEditing;
  const showPreview = type === 'branding' && showPreviewPanel && showFormContent;

  return (
    <DetailFrame
      breadcrumbs={templateBreadcrumbs}
      dropdownActions={[]}
      subscriptionActive={true}
      additionalHeaderElements={additionalHeaderElements}
      showBackButton={true}
      onBack={() => history.push('/templates')}
      className="flex-1 overflow-hidden"
    >
      <div className="flex h-full overflow-hidden">
        {/* ─── Left: Form Panel (matches BaseForm layout) ─── */}
        <FormikProvider value={formik}>
          <FormShell
            as="form"
            onSubmit={(e) => {
              e.preventDefault();
              (async () => {
                const errors = await formik.validateForm();
                if (Object.keys(errors).length > 0) {
                  formik.setTouched({ TemplateName: true, DraftType: true });
                  const msgs: string[] = [];
                  const collect = (obj: any) => {
                    Object.values(obj).forEach((v) => {
                      if (typeof v === 'string') msgs.push(v);
                      else if (typeof v === 'object' && v) collect(v);
                    });
                  };
                  collect(errors);
                  dispatch(showFeedbackModal({ modalType: 'error', message: msgs.slice(0, 3).join('. ') || t('Please fix the errors'), showModal: true, duration: 5000 }));
                  return;
                }
                formik.handleSubmit();
              })();
            }}
            className={`flex-1 min-h-0 transition-all duration-200 ${showPreview ? 'lg:w-1/2' : 'w-full'}`}
            title={
              isEditing
                ? t('Edit {{type}} Template', { type: type === 'branding' ? t('Branding') : t('Structure') })
                : t('New {{type}} Template', { type: type === 'branding' ? t('Branding') : t('Structure') })
            }
            description={
              type === 'branding'
                ? t('Configure fonts, margins, headers, footers, and logos for your documents.')
                : t('Define the section structure for your document template.')
            }
            footerClassName="justify-end"
            footer={
              /* Save / Cancel - always visible when the form shows */
              (showFormContent || type === 'structure') ? (
                <>
                  <Button variant="neutral" size="small" type="button" onClick={() => history.push('/templates')} disabled={isLoading}>
                    {t('Cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    type="submit"
                    disabled={isLoading}
                    isLoading={isLoading}
                  >
                    {isEditing ? t('Save Changes') : t('Create Template')}
                  </Button>
                </>
              ) : null
            }
          >

                  {/* ─── Section 1: Template Details ─── */}
                  <div>
                    <div className="mb-4">
                      <h3 className="text-sm text-fg-body">{t('Template Details')}</h3>
                      <p className="text-sm text-fg-muted mt-1">{t('Basic information about this template.')}</p>
                    </div>
                    <div className="space-y-4">
                      <Input
                        name="TemplateName"
                        value={formik.values.TemplateName}
                        onChange={formik.handleChange}
                        label={t('Template Name')}
                        error={formik.touched.TemplateName && formik.errors.TemplateName}
                        helperText={formik.touched.TemplateName && formik.errors.TemplateName}
                        required
                        type="text"
                        placeholder={t('e.g., Corporate Letter Template')}
                      />
                      <div className="grid grid-cols-2 gap-4">
                        <Dropdown
                          value={formik.values.DraftType}
                          onChange={(value) => formik.setFieldValue('DraftType', value)}
                          options={draftTypeOptions}
                          label={t('Document Type')}
                          error={formik.touched.DraftType && formik.errors.DraftType as string | false || undefined}
                          fullWidth
                        />
                      </div>
                    </div>
                  </div>

                  {!isEditing && projectOptions.length > 0 && (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-sm text-fg-body">{t('Link to Project')}</h3>
                        <p className="text-sm text-fg-muted mt-1">{t('Optionally link this template to an existing project.')}</p>
                      </div>
                      <Dropdown
                        value={selectedProjectUUID}
                        onChange={(value) => setSelectedProjectUUID(String(value || ''))}
                        options={[{ value: '', label: t('No project') }, ...projectOptions]}
                        label={t('Project')}
                        fullWidth
                      />
                    </div>
                  )}

                  {/* ─── Sharing Section: Org-Wide Toggle + Collaborators ─── */}
                  {!isEditing && (showFormContent || type === 'structure') && (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-sm text-fg-body">{t('Sharing')}</h3>
                        <p className="text-sm text-fg-muted mt-1">{t('Control who can access this template.')}</p>
                      </div>
                      <div className="space-y-4">
                        <OrgWideToggle
                          value={isOrgWide}
                          onChange={setIsOrgWide}
                          title={t('Organization-wide template')}
                        />
                        {!isOrgWide && (
                          <CollaboratorSelector
                            collaborators={collaborators}
                            onCollaboratorsChange={setCollaborators}
                            title=""
                            showOwnerSection={false}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  {type === 'structure' && (
                    <div>
                      <div className="mb-4">
                        <h3 className="text-sm text-fg-body">{t('Structure Sections')}</h3>
                        <p className="text-sm text-fg-muted mt-1">{t('Add sections and subsections as needed. A new field appears when you fill the last one.')}</p>
                      </div>
                      <FieldArray name="Structure">
                        {({ remove, push }) => (
                          <div className="space-y-3">
                            {formik.values.Structure.map((section, index) => (
                              <div key={index} className="bg-surface-sunken/70 border border-hairline-soft rounded-lg p-4 relative group">
                                {/* Remove button - top right */}
                                {formik.values.Structure.length > 1 && (
                                  <Button
                                    variant="neutral"
                                    size="small"
                                    onClick={() => remove(index)}
                                    ariaLabel={t('Remove section')}
                                    className="!absolute top-3 end-3 !p-1.5 !rounded-md !border-0 !bg-transparent text-fg-muted hover:!text-danger-500 hover:!bg-danger-50 dark:hover:!bg-danger-900/20 opacity-0 group-hover:opacity-100"
                                  >
                                    <PiTrashLight className="w-4 h-4" />
                                  </Button>
                                )}

                                <div className="flex items-start gap-3">
                                  <div className="flex items-center pt-2">
                                    <OrdinalBadge value={index + 1} />
                                  </div>
                                  <div className="flex-1">
                                    <Input
                                      name={`Structure.${index}.section`}
                                      value={section.section}
                                      onChange={(e) => {
                                        formik.handleChange(e);
                                        if (index === formik.values.Structure.length - 1 && e.target.value.trim() !== '') {
                                          push({ section: '', subsections: [] });
                                        }
                                      }}
                                      label={t('Section Name')}
                                      placeholder={t('e.g., 1. DEFINITIONS')}
                                      required
                                      type="text"
                                      error={
                                        formik.touched.Structure?.[index]?.section &&
                                        (typeof formik.errors.Structure === 'string'
                                          ? formik.errors.Structure
                                          : (formik.errors.Structure?.[index] as { section?: string })?.section) as string | false || undefined
                                      }
                                    />
                                  </div>
                                </div>

                                {/* Subsections */}
                                <div className="mt-3 ps-9">
                                  <FieldArray name={`Structure.${index}.subsections`}>
                                    {({ remove: removeSub, push: pushSub }) => (
                                      <div className="space-y-2">
                                        {/* Names the subsection list, not one input - heading, not <label>. */}
                                        <h5 className="text-sm font-medium text-fg-body">{t('Subsections')}</h5>
                                        {section.subsections.map((sub, subIndex) => (
                                          <div key={subIndex} className="flex items-center gap-2">
                                            <Input
                                              name={`Structure.${index}.subsections.${subIndex}`}
                                              value={sub}
                                              onChange={(e) => {
                                                formik.handleChange(e);
                                                if (subIndex === section.subsections.length - 1 && e.target.value.trim() !== '') {
                                                  pushSub('');
                                                }
                                              }}
                                              placeholder={t('e.g., 1.1 Sub Definition')}
                                              type="text"
                                              className="flex-1"
                                            />
                                            {section.subsections.length > 1 && (
                                              <Button
                                                variant="neutral"
                                                size="small"
                                                onClick={() => removeSub(subIndex)}
                                                ariaLabel={t('Remove subsection')}
                                                className="!p-1.5 !rounded !border-0 !bg-transparent text-fg-muted hover:!text-danger-500 hover:!bg-danger-50 dark:hover:!bg-danger-900/20"
                                              >
                                                <PiTrashLight className="w-4 h-4" />
                                              </Button>
                                            )}
                                          </div>
                                        ))}
                                        <Button variant="neutral" size="small" type="button" onClick={() => pushSub('')} icon={<PiPlusLight />}>
                                          {t('Add Subsection')}
                                        </Button>
                                      </div>
                                    )}
                                  </FieldArray>
                                </div>
                              </div>
                            ))}
                            {formik.values.Structure[formik.values.Structure.length - 1]?.section.trim() !== '' && (
                              <Button variant="neutral" size="small" type="button" onClick={() => push({ section: '', subsections: [] })} icon={<PiPlusLight />}>
                                {t('Add Section')}
                              </Button>
                            )}
                          </div>
                        )}
                      </FieldArray>
                      {formik.touched.Structure && typeof formik.errors.Structure === 'string' && (
                        <p className="text-danger-600 text-sm mt-2">{formik.errors.Structure}</p>
                      )}
                    </div>
                  )}

                  {/* ─── Section 2 (Branding): Creation Method + Config ─── */}
                  {type === 'branding' && (
                    <>
                      {/* Step 1: Creation method selector (new templates only) */}
                      {!isEditing && creationMode === 'choose' && (
                        <div>
                          <div className="mb-4">
                            <h3 className="text-sm text-fg-body">{t('Setup Method')}</h3>
                            <p className="text-sm text-fg-muted mt-1">{t('Choose to configure manually or upload an existing document to auto-detect styling.')}</p>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <button
                              type="button"
                              onClick={() => setCreationMode('manual')}
                              className="flex flex-col items-center p-5 border border-hairline rounded-lg hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-all group"
                            >
                              <PiPencilSimpleLight className="h-7 w-7 text-fg-muted group-hover:text-primary-500 mb-2" />
                              <span className="text-sm font-medium text-fg-strong">{t('Manual Setup')}</span>
                              <span className="text-xs text-fg-muted text-center mt-1">
                                {t('Configure fonts, margins, headers, and logos yourself')}
                              </span>
                            </button>
                            <button
                              type="button"
                              onClick={() => setCreationMode('upload')}
                              disabled={isExtracting}
                              className="flex flex-col items-center p-5 border border-hairline rounded-lg hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/10 transition-all group disabled:opacity-50"
                            >
                              <PiUploadSimpleLight className="h-7 w-7 text-fg-muted group-hover:text-primary-500 mb-2" />
                              <span className="text-sm font-medium text-fg-strong">{t('Upload Document')}</span>
                              <span className="text-xs text-fg-muted text-center mt-1">
                                {t('Extract branding from a Word or PDF document')}
                              </span>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Step 1b: Upload mode - file picker + explicit extract button */}
                      {!isEditing && creationMode === 'upload' && (
                        <div>
                          <div className="mb-4">
                            <h3 className="text-sm text-fg-body">{t('Upload Template Document')}</h3>
                            <p className="text-sm text-fg-muted mt-1">
                              {t("Upload a .docx or .pdf file, then click Extract to auto-detect fonts, margins, headers, footers, and logos.")}
                            </p>
                          </div>
                          <div className="space-y-4">
                            <FileUploader
                              files={uploadedFiles}
                              onFilesChange={handleUploadFilesChange}
                              allowedExtensions={['docx', 'pdf']}
                            />

                            {/* Action buttons */}
                            <div className="flex items-center justify-between">
                              <Button
                                variant="neutral"
                                size="small"
                                type="button"
                                onClick={() => { setCreationMode('choose'); setUploadedFiles([]); }}
                                icon={<PiArrowLeftLight className="rtl:-scale-x-100" />}
                              >
                                {t('Back')}
                              </Button>

                              <Button
                                variant="primary"
                                size="small"
                                type="button"
                                onClick={handleExtractClick}
                                disabled={uploadedFiles.length === 0 || isExtracting}
                                isLoading={isExtracting}
                                icon={isExtracting ? undefined : <PiMagicWandLight className="h-4 w-4" />}
                              >
                                {isExtracting ? t('Extracting...') : t('Extract Branding')}
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Extraction success banner */}
                      {creationMode === 'manual' && extractedConfidence && (
                        <div className="rounded-lg border border-success-200 dark:border-success-800 bg-success-50 dark:bg-success-900/20 p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <PiCheckCircleLight className="h-5 w-5 text-success-600 dark:text-success-400 flex-shrink-0" />
                              <div>
                                <p className="text-sm font-medium text-success-800 dark:text-success-300">
                                  {t('Branding extracted from')} <span className="font-mono text-xs">{extractedFilename}</span>
                                </p>
                                <p className="text-xs text-success-600 dark:text-success-400 mt-0.5">
                                  {t('Review and adjust the extracted settings below.')}
                                </p>
                              </div>
                            </div>
                            {!isEditing && (
                              <Button
                                variant="neutral"
                                size="small"
                                type="button"
                                onClick={() => { setCreationMode('choose'); setExtractedConfidence(null); setExtractedFilename(''); }}
                                icon={<PiArrowLeftLight className="rtl:-scale-x-100" />}
                              >
                                {t('Start Over')}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Back button for manual mode (no extraction, new template) */}
                      {!isEditing && creationMode === 'manual' && !extractedConfidence && (
                        <Button variant="neutral" size="small" type="button" onClick={() => setCreationMode('choose')} icon={<PiArrowLeftLight className="rtl:-scale-x-100" />}>
                          {t('Back')}
                        </Button>
                      )}

                      {/* Branding configuration panel */}
                      {showFormContent && (
                        <BrandingPanel
                          t={t}
                          formik={formik}
                          fontOptions={fontOptions}
                          headingStyleOptions={headingStyleOptions}
                          extractedConfidence={extractedConfidence}
                        />
                      )}

                      {/* Wave 5 - offer to re-apply an edited brand to the
                          finalized documents that used an older version. Only
                          meaningful for an already-saved branding template. */}
                      {showFormContent && isEditing && (
                        <BrandingReapplyBanner templateId={templateId} refreshKey={templateId} />
                      )}
                    </>
                  )}

                  {/* ─── Collaborators Section (editing - managed via header menu) ─── */}

          </FormShell>
        </FormikProvider>

        {/* ─── Right: Preview Panel (branding only, toggleable) ─── */}
        {showPreview && (
          <div className="hidden lg:flex flex-col w-[45%] min-h-0 border-s border-hairline bg-surface-soft">
            <div className="flex items-center justify-between px-4 py-3 border-b border-hairline flex-shrink-0">
              <span className="text-xs text-fg-muted">{t('Live Preview')}</span>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-4 text-xs text-fg-muted">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 border-t border-dashed border-fg-muted"></div>
                    <span>{t('Margins')}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 bg-surface-sunken border border-hairline rounded-sm"></div>
                    <span>{t('Content')}</span>
                  </div>
                </div>
                <Button
                  variant="neutral"
                  size="small"
                  type="button"
                  onClick={() => dispatch(showModal({
                    type: 'templatePreview',
                    showModal: true,
                    message: t('Template Preview'),
                    templateBranding: formik.values.Branding,
                    width: '90vw',
                  }))}
                  icon={<PiEyeLight className="h-3.5 w-3.5" />}
                  className="text-xs"
                >
                  {t('Full Preview')}
                </Button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar-main p-4">
              <BrandingPreview branding={formik.values.Branding} />
            </div>
          </div>
        )}
      </div>
    </DetailFrame>
  );
};

export default TemplateForm;